import { useCallback, useMemo, useState } from "react";
import DSButton from "../../../system-design/DSButton";
import DSCheckbox from "../../../system-design/DSCheckBox";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSFormControlLabel from "../../../system-design/DSFormControlLabel";
import DSStack from "../../../system-design/DSStack";
import DSTypography from "../../../system-design/DSTypography";

enum ResetOption {
  ClearDeviceSettings = "ClearDeviceSettings",
  ClearGift = "ClearGift",
  ClearOrder = "ClearOrder",
}

const RESET_OPTIONS: { key: ResetOption; label: string }[] = [
  {
    key: ResetOption.ClearDeviceSettings,
    label: "Clear device settings (this will clear Firestore)",
  },
  {
    key: ResetOption.ClearGift,
    label:
      "Clear gift (this will remove clockId from gift, and mark it as manualOverride)",
  },
  {
    key: ResetOption.ClearOrder,
    label:
      "Clear order (this will remove clockId from order, and mark it as manual override)",
  },
];

type ResetDeviceDialogProps = {
  open: boolean;
  onClose: () => void;
  deviceIdf: string;
  resetDeviceData: () => Promise<void>;
  clearGift: () => Promise<void>;
  clearOrder: () => Promise<void>;
};

const ResetDeviceDialog = ({
  open,
  onClose,
  deviceIdf,
  resetDeviceData,
  clearGift,
  clearOrder,
}: ResetDeviceDialogProps) => {
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ResetOption[]>([]);

  const actionMap: Record<ResetOption, () => Promise<void>> = useMemo(
    () => ({
      [ResetOption.ClearDeviceSettings]: resetDeviceData,
      [ResetOption.ClearGift]: clearGift,
      [ResetOption.ClearOrder]: clearOrder,
    }),
    [resetDeviceData, clearGift, clearOrder],
  );

  const toggleOption = useCallback((option: ResetOption, checked: boolean) => {
    setSelected((prev) =>
      checked
        ? [...prev, option]
        : prev.filter((selectedOption) => selectedOption !== option),
    );
  }, []);

  const allSelected = selected.length === RESET_OPTIONS.length;

  const toggleAll = useCallback((checked: boolean) => {
    setSelected(checked ? RESET_OPTIONS.map(({ key }) => key) : []);
  }, []);

  const resetState = useCallback(() => {
    setSelected([]);
    setWarning("");
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleReset = useCallback(async () => {
    setLoading(true);
    setWarning("");
    try {
      await Promise.all(selected.map((option) => actionMap[option]()));
      handleClose();
    } catch (error) {
      setWarning((error as Error).message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [selected, actionMap, handleClose]);

  return (
    <DSDialog open={open} onClose={handleClose}>
      <DSDialogContent>
        <DSTypography variant="h6">
          Are you sure you want to reset the cloud data of this device?
        </DSTypography>
        <DSTypography variant="subtitle1">{deviceIdf}</DSTypography>
        <DSStack sx={{ mt: 1 }}>
          <DSFormControlLabel
            control={
              <DSCheckbox
                checked={allSelected}
                indeterminate={selected.length > 0 && !allSelected}
                onChange={(e) => toggleAll(e.target.checked)}
              />
            }
            label="Delete all"
          />
          {RESET_OPTIONS.map(({ key, label }) => (
            <DSFormControlLabel
              key={key}
              control={
                <DSCheckbox
                  checked={selected.includes(key)}
                  onChange={(e) => toggleOption(key, e.target.checked)}
                />
              }
              label={label}
            />
          ))}
        </DSStack>
        {warning && (
          <DSTypography color="error" sx={{ mt: 1 }}>
            {warning}
          </DSTypography>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSButton onClick={handleClose}>Cancel</DSButton>
        <DSErrorButton
          disabled={loading || selected.length === 0}
          onClick={handleReset}
        >
          {loading && <DSCircularProgress size={16} sx={{ mr: 1 }} />}
          Reset
        </DSErrorButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default ResetDeviceDialog;
